import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    onAuthStateChanged,
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signOut,
} from 'firebase/auth';
import { Modal, Button, Alert } from 'react-bootstrap';

import { auth } from '../firebaseConfig';
import { settings } from '../config/settings';
import { setForbiddenHandler, setTokenFunctions, apiFetch } from '../utils/apiClient';

const AuthContext = createContext({
    user: null,
    loading: true,
    authError: null,
    authPending: false,
    loginWithEmailPassword: async () => {},
    registerWithEmailPassword: async () => {},
    logout: async () => {},
});

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [authError, setAuthError] = useState(null);
    const [authPending, setAuthPending] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusModalMessage, setStatusModalMessage] = useState('');

    // Function to show 403 Forbidden modal
    const showForbiddenModal = useCallback(() => {
        setStatusModalMessage('This user is not authorized.');
        setShowStatusModal(true);
    }, []);

    // Register 403 handler with API client
    useEffect(() => {
        setForbiddenHandler(showForbiddenModal);
    }, [showForbiddenModal]);

    // Helper function to sync user with backend
    const syncUserWithBackend = useCallback(async (user) => {
        if (!user) return;

        try {
            const idToken = await user.getIdToken();
            const syncEndpoint = `${settings.api.baseUrl}${settings.api.users.sync.apiEndpoint}`;
            const response = await apiFetch(syncEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`,
                },
                body: JSON.stringify({
                    email: user.email,
                    firebaseUid: user.uid,
                    origin: window.location.origin,
                }),
            });

            // Check for 403 Forbidden response (apiFetch already handles this, but we check here too for early return)
            if (response.status === 403) {
                return;
            }

            if (!response.ok) {
                console.warn('User sync failed:', response.status);
            } else {
                const data = await response.json();
                console.log('User synced successfully', data);
                
                // Check user status from response
                if (data && data.user && data.user.status) {
                    const status = data.user.status.toLowerCase();
                    
                    // For Campus Client: if status is pending, show pending message
                    if (status === 'pending') {
                        setStatusModalMessage('This user is pending authorization. Ask your Campus administrator.');
                        setShowStatusModal(true);
                    }
                    // If status is not active or pending, show not authorized message
                    else if (status !== 'active' && status !== 'pending') {
                        setStatusModalMessage('This user is not authorized.');
                        setShowStatusModal(true);
                    }
                }
                
                // Force token refresh to get updated claims
                await user.getIdToken(true);
            }
        } catch (error) {
            console.error('Error syncing user:', error);
        }
    }, [showForbiddenModal]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setUser(currentUser);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const loginWithEmailPassword = useCallback(async (email, password) => {
        setAuthError(null);
        setAuthPending(true);
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            // Sync user with backend after successful login
            await syncUserWithBackend(userCredential.user);
        } catch (error) {
            console.error('Email sign-in failed', error);
            setAuthError(error.message ?? 'No fue posible iniciar sesión.');
            throw error;
        } finally {
            setAuthPending(false);
        }
    }, [syncUserWithBackend]);

    const registerWithEmailPassword = useCallback(async (email, password) => {
        setAuthError(null);
        setAuthPending(true);
        try {
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            // Sync user with backend after successful registration
            // Email verification is now handled by the backend
            await syncUserWithBackend(userCredential.user);
        } catch (error) {
            console.error('Email registration failed', error);
            setAuthError(error.message ?? 'No fue posible crear la cuenta.');
            throw error;
        } finally {
            setAuthPending(false);
        }
    }, [syncUserWithBackend]);

    const logout = useCallback(async () => {
        setAuthError(null);
        setAuthPending(true);
        try {
            await signOut(auth);
        } catch (error) {
            console.error('Sign-out failed', error);
            setAuthError(error.message ?? 'No fue posible cerrar sesión.');
            throw error;
        } finally {
            setAuthPending(false);
        }
    }, []);

    const getIdToken = useCallback(async (forceRefresh = false) => {
        if (!auth.currentUser) {
            return null;
        }

        return auth.currentUser.getIdToken(forceRefresh);
    }, []);

    const getClaims = useCallback(async (forceRefresh = false) => {
        if (!auth.currentUser) {
            return null;
        }

        try {
            const token = await auth.currentUser.getIdToken(forceRefresh);
            if (!token) {
                return null;
            }

            // Decode JWT to get claims
            const parts = token.split('.');
            if (parts.length !== 3) {
                return null;
            }

            const payload = parts[1];
            let base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
            const padding = base64.length % 4;
            if (padding) {
                base64 += '='.repeat(4 - padding);
            }

            const decoded = atob(base64);
            return JSON.parse(decoded);
        } catch (error) {
            console.error('Error getting claims:', error);
            return null;
        }
    }, []);

    const refreshToken = useCallback(async () => {
        if (!auth.currentUser) {
            return null;
        }

        try {
            // Force refresh token to get updated claims
            await auth.currentUser.getIdToken(true);
            return true;
        } catch (error) {
            console.error('Error refreshing token:', error);
            return false;
        }
    }, []);

    // Register token functions with API client for automatic token refresh
    // This must be after getIdToken and refreshToken are defined
    useEffect(() => {
        setTokenFunctions(getIdToken, refreshToken);
    }, [getIdToken, refreshToken]);

    const value = {
        user,
        loading,
        authError,
        authPending,
        loginWithEmailPassword,
        registerWithEmailPassword,
        getIdToken,
        getClaims,
        refreshToken,
        logout,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
            
            {/* Status Modal */}
            <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Authorization Status</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning" className="mb-0">
                        {statusModalMessage}
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowStatusModal(false)}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);

